import { useLazyQuery } from '@apollo/client';
import envConfig from '@hulu/env-config';
import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { useCallback, useState } from 'react';

import { GET_ORGANIZATIONS_BY_USER } from '../../apis/graphql/organization/organizationsByUser';
import { GetOrganizationsByUserQuery, GetOrganizationsByUserQueryVariables } from '../../apis/graphql/types';
import useUpdateUserPhone from '../../apis/graphql/user/useUpdateUserPhone/useUpdateUserPhone';
import admanToast from '../../components/Alert/util/admanToast';
import { defaultErrorMessage } from '../../constants/global';
import { UserInfo } from '../../models/UserAWSCognitoInfo';
import { handleGql403 } from '../../utils';
import { rerouteToHubIfNeeded } from '../../utils/rerouteToHubIfNeeded';
import { useAWSCognitoUser } from '../index';
import { AwsAttributeName } from '../useAWSCognitoUser/constants';
import { MFAMethod, MFAStep } from './constants';
import { LoginValues } from './types';

export type MFAResponse = {
  mfaStep: MFAStep | null;
  loading: boolean;
  setPhone: (phoneNumber: string) => void;
  onLogin: (email: string, password: string) => void;
  verifyCode: (smsCode: string) => void;
  resendCode: () => void;
  onSetPassword: (email: string, token: string, newPassword: string) => void;
  errorToShow: string;
  setErrorToShow: (errorToShow: string) => void;
  setupMfaFlow: boolean;
  passwordStep: boolean;
  cognitoUser: CognitoUser | null;
  onResetPassword: (password: string) => void;
  oldPassword: string;
  setOldPassword: (oldPassword: string) => void;
};

export type Props = {
  onAfterMFA: () => void;
  saveDevice?: boolean;
};

const useMFA = ({ onAfterMFA, saveDevice = false }: Props): MFAResponse => {
  const [mfaStep, setMFAStep] = useState<MFAStep | null>(null);
  const [errorToShow, setErrorToShow] = useState('');
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordStep, setPasswordStep] = useState(false);
  const [setupMfaFlow, setSetupMfaFlow] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [getUserOrganizations] = useLazyQuery<GetOrganizationsByUserQuery, GetOrganizationsByUserQueryVariables>(
    GET_ORGANIZATIONS_BY_USER,
    { onError: handleGql403 }
  );
  const [credentials, setCredentials] = useState<LoginValues | null>(null);
  const { handleUpdateUserPhone } = useUpdateUserPhone();

  const {
    login,
    getUserPreferredMFAMethod,
    verifySmsCode,
    setAmsUser,
    getUserAttribute,
    verifyAttribute,
    initiateAttributeVerification,
    setMFA,
    logout,
    setInitialPassword,
    getPreferredMFAByCognitoUser,
  } = useAWSCognitoUser();

  const onLoggedIn = useCallback(
    async (cognitoUser: CognitoUser) => {
      setAmsUser(cognitoUser);
      await rerouteToHubIfNeeded(cognitoUser);
      onAfterMFA();
    },
    [onAfterMFA, setAmsUser]
  );

  const initMFA = useCallback(
    async (cognitoUser: CognitoUser): Promise<void> => {
      try {
        if (cognitoUser.challengeName === MFAMethod.SMS_MFA) {
          setMFAStep(MFAStep.ENTER_SMS_CODE);

          return;
        }

        const prefferedMFA = await getUserPreferredMFAMethod(cognitoUser);
        if (prefferedMFA === MFAMethod.SMS_MFA) {
          onLoggedIn(cognitoUser);
          return;
        }

        if (prefferedMFA === MFAMethod.NOMFA) {
          setSetupMfaFlow(true);

          if (envConfig.REACT_APP_ENABLE_2FA_FOR_BRUNO !== 'true') {
            const userOrganizations = await getUserOrganizations();
            if (!userOrganizations.data) return;
            const userRelatesToBrunoOrganization = userOrganizations.data.organizationsByUser.some(
              (organization) => !organization.isBaymaxEnabled
            );

            if (userRelatesToBrunoOrganization) {
              await logout(false);
              window.location.href = envConfig.REACT_APP_HAM_CLASSIC;
            }
          }

          const phoneNumber = await getUserAttribute(cognitoUser, AwsAttributeName.PHONE_NUMBER);

          if (!!phoneNumber) {
            await initiateAttributeVerification(cognitoUser, AwsAttributeName.PHONE_NUMBER);
            setMFAStep(MFAStep.ENTER_SMS_CODE);
          } else {
            setMFAStep(MFAStep.SET_PHONE);
          }
          return;
        }
        admanToast.error(defaultErrorMessage);
      } catch {
        admanToast.error(defaultErrorMessage);
      }
    },
    [
      getUserAttribute,
      getUserOrganizations,
      getUserPreferredMFAMethod,
      initiateAttributeVerification,
      logout,
      onLoggedIn,
    ]
  );

  const onSetPassword = useCallback(
    async (email: string, token: string, newPassword: string): Promise<void> => {
      setLoading(true);

      try {
        const cognitoUser = await setInitialPassword(email, token, newPassword);
        setCognitoUser(cognitoUser);
        if (envConfig.REACT_APP_DISABLE_2FA === 'true') {
          onLoggedIn(cognitoUser);

          return;
        }
        await initMFA(cognitoUser);
      } catch (error: unknown) {
        admanToast.error(defaultErrorMessage);
      } finally {
        setLoading(false);
      }
    },
    [initMFA, onLoggedIn, setInitialPassword]
  );

  const onResetPassword = async (newPassword: string): Promise<void> => {
    const authenticationDetails = new AuthenticationDetails({
      Username: cognitoUser?.getUsername() || '',
      Password: oldPassword,
    });

    cognitoUser?.authenticateUser(authenticationDetails, {
      onSuccess: () => {
        onLoggedIn(cognitoUser);
        return;
      },
      onFailure: (err) => {
        console.error('Authentication failed:', err);
        return;
      },
      newPasswordRequired: (userAttributes) => {
        console.log('New password required!');
        const updatedAttributes = {
          // Include any required attributes here
          email: userAttributes.email,
        };
        cognitoUser.completeNewPasswordChallenge(newPassword, updatedAttributes, {
          onSuccess: () => {
            console.log('Password updated successfully!');
            onLoggedIn(cognitoUser);
            return;
          },
          onFailure: (err) => {
            console.error('Failed to set new password:', err);
            return;
          },
        });
        return;
      },
    });
  };

  const onLogin = useCallback(
    async (email: string, password: string): Promise<void> => {
      setLoading(true);
      try {
        const cognitoUser = await login(email.toLowerCase(), password);
        setCognitoUser(cognitoUser);
        if (cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setOldPassword(password);
          setPasswordStep(true);
          return;
        }

        if (envConfig.REACT_APP_DISABLE_2FA === 'true' && cognitoUser.challengeName !== MFAMethod.SMS_MFA) {
          onLoggedIn(cognitoUser);

          return;
        }
        setCredentials({
          email,
          password,
        });
        await initMFA(cognitoUser);
      } catch (error: unknown) {
        const notValidEmailPasswordErrors = ['UserNotFoundException', 'NotAuthorizedException'];

        const errorToShow = notValidEmailPasswordErrors.includes((error as Error).message)
          ? 'Invalid email or password. Please try again.'
          : defaultErrorMessage;

        admanToast.error(errorToShow);
      } finally {
        setLoading(false);
      }
    },
    [initMFA, login, onLoggedIn]
  );

  const setPhone = useCallback(
    async (phoneNumber: string): Promise<void> => {
      const updatePhoneNumberErrorMessage =
        'Something went wrong in process of setting phone number. Please try again later.';
      setLoading(true);
      if (!cognitoUser || !phoneNumber) {
        admanToast.error(updatePhoneNumberErrorMessage);

        return;
      }

      try {
        const session = cognitoUser.getSignInUserSession();
        const userId: string = (session?.getIdToken().decodePayload() as UserInfo)?.ams_user_id;
        if (!userId) {
          admanToast.error(updatePhoneNumberErrorMessage);

          return;
        }
        await handleUpdateUserPhone(userId, phoneNumber);
        await initiateAttributeVerification(cognitoUser, AwsAttributeName.PHONE_NUMBER);
        setMFAStep(MFAStep.ENTER_SMS_CODE);
      } catch {
        admanToast.error(updatePhoneNumberErrorMessage);
      } finally {
        setLoading(false);
      }
    },
    [cognitoUser, handleUpdateUserPhone, initiateAttributeVerification]
  );

  const verifyCode = useCallback(
    async (smsCode: string): Promise<void> => {
      const defaultError = 'Something went wrong during verification code process. Please try again later.';
      setErrorToShow('');
      setLoading(true);
      if (!cognitoUser) {
        admanToast.error(defaultError);

        return;
      }

      try {
        if (setupMfaFlow) {
          await verifyAttribute(cognitoUser, AwsAttributeName.PHONE_NUMBER, smsCode);
          await setMFA(cognitoUser);
          setMFAStep(MFAStep.MULTI_FACTOR_AUTH_ENABLED);
          setLoading(false);
          setTimeout(() => {
            onLoggedIn(cognitoUser);
          }, 3000);
        } else {
          const newCognitoUser = await verifySmsCode(cognitoUser, smsCode);
          const preferredMFA = await getPreferredMFAByCognitoUser(cognitoUser);
          if (!preferredMFA) {
            //It is needed here for cases when user set MFA directly in Cognito. For some reason in this case preferred MFA is empty
            await setMFA(newCognitoUser);
          }
          setCognitoUser(newCognitoUser);
          if (saveDevice) {
            try {
              await Auth.rememberDevice();
            } catch (e) {
              admanToast.error('Something went wrong during saving device. Please try again later.');
            }
          }
          onLoggedIn(newCognitoUser);
        }
      } catch (error: unknown) {
        const notValidCodeErrors = ['CodeMismatchException'];

        setLoading(false);
        if (notValidCodeErrors.includes((error as Error).message)) {
          setErrorToShow('The verification code is incorrect or expired.');

          return;
        }

        admanToast.error(defaultError);
      }
    },
    [
      cognitoUser,
      getPreferredMFAByCognitoUser,
      onLoggedIn,
      saveDevice,
      setMFA,
      setupMfaFlow,
      verifyAttribute,
      verifySmsCode,
    ]
  );

  const resendCode = useCallback(async (): Promise<void> => {
    const resendCodeErrorMessage = 'Something went wrong in process of resending code. Please try again later.';
    setLoading(true);
    try {
      if (!cognitoUser) {
        admanToast.error(resendCodeErrorMessage);

        return;
      }

      if (setupMfaFlow) {
        await initiateAttributeVerification(cognitoUser, AwsAttributeName.PHONE_NUMBER);
      } else {
        if (!credentials) {
          admanToast.error(resendCodeErrorMessage);

          return;
        }
        const newCognitoUser = await login(credentials.email, credentials.password);
        setCognitoUser(newCognitoUser);
      }
    } catch (error: unknown) {
      admanToast.error(resendCodeErrorMessage);
    } finally {
      setLoading(false);
    }
  }, [cognitoUser, credentials, initiateAttributeVerification, login, setupMfaFlow]);

  return {
    mfaStep,
    loading,
    setPhone,
    onLogin,
    verifyCode,
    resendCode,
    onSetPassword,
    errorToShow,
    setErrorToShow,
    setupMfaFlow,
    passwordStep,
    cognitoUser,
    onResetPassword,
    oldPassword,
    setOldPassword,
  };
};

export default useMFA;
